import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { skipToken } from '@apollo/client/react';
import { Col } from '@opendoor/atoms/box';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { CustomerTosStatusDocument, SetCustomerTosAcceptedDocument } from '__generated__/graphql';
import { useMutation, useQuery } from 'platform/apollo';
import { Button, Link } from 'platform/atoms';
import { toError } from 'platform/Error';
import { useObservability } from 'platform/observability';
import { TERMS_OF_SERVICE_URL } from 'shared/helpers/externalRoutes';
import { useBrowserCompatibilityData } from 'shared/helpers/utils';

import { useAuthContext } from './AuthContext';

/**
 * Hook to retrieve data and functions to use with the TOS Modal
 *
 * @returns TOSModalContext data
 */
export const useTOSContext = () => useContext(TOSContext);

type TOSContext = {
  /**
   * Gives pages the ability to override if the TOS modal should be shown
   * Helpful for pages like loading,error when it should never be shown
   */
  forceHideTOSModal: (hideModal: boolean) => void;
  /**
   * Determines if the TOS modal should be shown
   */
  showTOSModal: boolean;
  /**
   * adds a loading state to the customer TOS button on acceptance
   */
  isTOSAcceptanceLoading: boolean;
  /**
   * Called when the customer accepts the TOS and updates the status
   */
  customerAcceptsTOS: () => void;
};

const TOSContext = createContext({
  forceHideTOSModal: (_hideModal: boolean) => {},
  showTOSModal: false,
  isTOSAcceptanceLoading: false,
  customerAcceptsTOS: () => {},
});
const IGNORE_TOS_MODAL_SET_IS_OPEN = () => {};

export const TOSProvider: FC<PropsWithChildren> = ({ children }) => {
  const { trackError } = useObservability();
  const { state, user } = useAuthContext();
  const [showTOSModal, setShowTOSModal] = useState(false);
  const [isTOSAcceptanceLoading, setCustomerAcceptedLoading] = useState(false);
  const [tosModalOverriden, setTOSModalOverriden] = useState(false);
  const tosTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(
    () => () => {
      for (const id of tosTimeoutsRef.current) {
        clearTimeout(id);
      }
      tosTimeoutsRef.current.clear();
    },
    [],
  );

  /**
   * Determines if the Customer should see the TOS modal
   * Skips checking the TOSStatusEndpoint if it's a webview or use is not authenticated
   * We store the result in state - tosUpdateRequired and update the state if customer accepts TOS - so we don't show the TOS modal again while the backend updates the status
   */
  const [tosUpdateRequired, settosUpdateRequired] = useState(false);

  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  const { data, loading } = useQuery(
    CustomerTosStatusDocument,
    state !== 'authenticated' || isOpendoorAppEvoWebView || user?.impersonated
      ? skipToken
      : {
          context: { linkContext: { team: 'seller' } },
        },
  );

  const updateRequired = !loading && !!data?.customerTos.getTOSStatus?.updateRequired;
  useEffect(() => {
    settosUpdateRequired(updateRequired);
  }, [updateRequired]);

  useEffect(() => {
    if (tosUpdateRequired && !tosModalOverriden) {
      setShowTOSModal(true);
    }

    return () => setShowTOSModal(false);
  }, [tosUpdateRequired, tosModalOverriden]);

  /**
   * This function gives pages the ability to override if the TOS modal should be shown
   * Helpful for pages like loading or error when it should never be shown
   */
  const forceHideTOSModal = useCallback(
    (hideModal: boolean) => {
      if (hideModal) {
        setShowTOSModal(!hideModal);
        // this prevents us from pre-maturely flashing the modal on the loading page
        setTOSModalOverriden(true);
      } else {
        setShowTOSModal(tosUpdateRequired);
        setTOSModalOverriden(false);
      }
    },
    [tosUpdateRequired],
  );

  /**
   * Customer accepts the TOS and updates the status
   */
  const [setCustomerTOSAccepted] = useMutation(SetCustomerTosAcceptedDocument, {
    context: { linkContext: { team: 'seller' } },
  });

  const customerAcceptsTOS = useCallback(() => {
    // ensures a customer can't click the button multiple times so we're setting the loading state to true
    setCustomerAcceptedLoading(true);
    // tracks an error if the mutation takes more than 10 seconds
    const timeoutId = setTimeout(() => {
      tosTimeoutsRef.current.delete(timeoutId);
      // eslint-disable-next-line @opendoor/track-error-prefer-throw -- TOS timeout telemetry keeps the modal flow active while the mutation may still finish.
      trackError(
        'set customer TOS accepted error',
        new Error('setCustomerTOSAccepted mutation took more than 10 seconds'),
      );
    }, 10000);
    tosTimeoutsRef.current.add(timeoutId);

    setCustomerTOSAccepted()
      .then(() => {
        setShowTOSModal(false);
        settosUpdateRequired(false);
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line @opendoor/track-error-prefer-throw -- TOS failures keep the modal flow active for retry.
        trackError('set customer TOS accepted error', toError(error));
      })
      .finally(() => {
        setCustomerAcceptedLoading(false);
        // if setCustomerTOSAccepted is successful or fails before the timeout clear the timeout
        clearTimeout(timeoutId);
        tosTimeoutsRef.current.delete(timeoutId);
      });
  }, [setCustomerTOSAccepted, trackError]);

  const context = useMemo(
    () => ({
      forceHideTOSModal,
      showTOSModal,
      isTOSAcceptanceLoading,
      customerAcceptsTOS,
    }),
    [customerAcceptsTOS, forceHideTOSModal, isTOSAcceptanceLoading, showTOSModal],
  );

  return <TOSContext.Provider value={context}>{children}</TOSContext.Provider>;
};

export const TOSModal: FC = () => {
  const { showTOSModal, customerAcceptsTOS, isTOSAcceptanceLoading } = useTOSContext();

  return (
    <Dialog.Root
      role="dialog"
      open={showTOSModal}
      onOpenChange={IGNORE_TOS_MODAL_SET_IS_OPEN}
      disablePointerDismissal
    >
      <Dialog title="We’ve updated our terms" hideCloseButton>
        <Col analyticsName="TOS-Modal" trackImpression className="gap-6">
          {/* this Col's maxWidth is slightly larger than the title's length */}
          <Col className="gap-3 md:gap-4 md:max-w-137">
            <Text as="p" className="text-body-md">
              We encourage you to review our updated{' '}
              <Link
                aria-label="open Opendoor’s terms of service in a new window"
                href={TERMS_OF_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
                analyticsName="tos-modal-link"
                className="whitespace-nowrap"
                trackImpression
              >
                Terms of Service
              </Link>
              . These Terms contain an arbitration agreement under which both you and Opendoor agree
              to resolve disputes through final and binding arbitration on an individual basis, and
              not by way of a lawsuit in court or a class action of any kind.
            </Text>
            <Text as="p" className="text-body-md">
              By continuing, you agree to the updated Terms.
            </Text>
          </Col>
          <Button
            onClick={customerAcceptsTOS}
            aria-label="Accept the Terms and Conditions"
            analyticsName="accept-tos"
            loading={isTOSAcceptanceLoading}
          >
            Continue
          </Button>
        </Col>
      </Dialog>
    </Dialog.Root>
  );
};
